import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, Users, RefreshCw, TrendingUp, AlertTriangle } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { useRefresh } from "@/contexts/RefreshContext";
import { insightsService } from "@/services/insightsService";

interface TeamInsight {
  type: "opportunity" | "concern" | "success";
  title: string;
  description: string;
  actionable: string;
  priority: "high" | "medium" | "low";
}

const TeamAISuggestionsWidget = () => {
  const { user } = useUser();
  const { lastCheckInTime } = useRefresh();
  const [insights, setInsights] = useState<TeamInsight[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // ALIGNED WITH web_old: Generate team insights using backend API with AI
  // Backend endpoint: GET /insights/team-insights?forceRefresh=false
  // MIGRATED FROM web_old/src/components/dashboard/TeamAISuggestionsWidget.tsx::generateTeamInsights()
  const generateTeamInsights = async (forceRefresh = false): Promise<TeamInsight[]> => {
    if (!user?.teamId) {
      return [
        {
          type: "opportunity",
          title: "Join a Team",
          description: "Team insights are available when you're part of a team.",
          actionable: "Join a team to unlock team collaboration features.",
          priority: "medium"
        }
      ];
    }

    try {
      // ALIGNED WITH web_old: Call backend endpoint for AI-generated team insights (lines 116-131)
      // Backend handles: fetching team check-ins, calculating metrics, calling OpenAI, and fallback
      // Backend returns TeamInsight[] directly (AI-generated or fallback if AI fails)
      const insights = await insightsService.getTeamInsights(forceRefresh);
      
      // Backend already includes fallback logic, so return insights directly
      return insights.length > 0 ? insights : generateFallbackInsights(3, 50, 0, 0, 0);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error generating team insights:", error);
      }
      // ALIGNED WITH web_old: Fallback to default error insight (lines 188-196)
      return [
        {
          type: "opportunity",
          title: "Enable Team Insights",
          description: "Unable to load team data for AI analysis.",
          actionable: "Check your team setup and try again.",
          priority: "medium"
        }
      ];
    }
  };

  // ALIGNED WITH web_old: Fallback insights (kept for local fallback if backend fails completely)
  // ALIGNED WITH web_old/src/components/dashboard/TeamAISuggestionsWidget.tsx::generateFallbackInsights()
  // Note: Backend also has fallback logic, this is a final fallback
  const generateFallbackInsights = (
    avgMood: number, 
    avgEnergy: number, 
    blockerCount: number, 
    helpRequestCount: number,
    activeMembers: number,
    moodStats?: { good: number; okay: number; low: number }
  ): TeamInsight[] => {
    const insights: TeamInsight[] = [];

    // ALIGNED WITH web_old: Same fallback logic (lines 134-178)
    if (avgMood < 3) {
      insights.push({
        type: "concern",
        title: "Team Mood Below Average",
        description: `Team mood is ${avgMood.toFixed(1)}/5, indicating some challenges.`,
        actionable: "Consider a team retrospective or one-on-ones to address concerns.",
        priority: "high"
      });
    }

    if (blockerCount > 0) {
      insights.push({
        type: "opportunity",
        title: "Help Remove Team Blockers",
        description: `${blockerCount} blockers were mentioned by team members recently.`,
        actionable: "Schedule a blocker resolution session to tackle these together.",
        priority: "high"
      });
    }

    if (helpRequestCount > 0) {
      insights.push({
        type: "opportunity",
        title: "Foster Peer Support",
        description: `${helpRequestCount} team members requested help recently.`,
        actionable: "Create a buddy system or skill-sharing sessions.",
        priority: "medium"
      });
    }

    if (insights.length === 0) {
      insights.push({
        type: "success",
        title: "Team is Doing Well",
        description: "Your team shows positive engagement and wellbeing indicators.",
        actionable: "Keep up the great work and maintain regular check-ins.",
        priority: "low"
      });
    }

    return insights.slice(0, 3);
  };

  const loadTeamInsights = async (forceRefresh = false) => {
    if (!user?.teamId) {
      setInsights([]);
      return;
    }

    setIsLoading(true);
    try {
      const teamInsights = await generateTeamInsights(forceRefresh);
      setInsights(teamInsights);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error loading team insights:", error);
      }
      setInsights([
        {
          type: "opportunity",
          title: "Enable Team Insights",
          description: "Unable to load team data for AI analysis.",
          actionable: "Check your team setup and try again.",
          priority: "medium"
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.teamId) {
      loadTeamInsights();
    }
  }, [user?.teamId]);

  // Refresh insights when a new check-in is submitted
  useEffect(() => {
    if (lastCheckInTime && user?.teamId) {
      loadTeamInsights();
    }
  }, [lastCheckInTime]);

  const getInsightIcon = (type: string) => {
    switch (type) {
      case "concern":
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case "success":
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      default:
        return <Lightbulb className="h-4 w-4 text-blue-500" />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case "concern":
        return "border-red-200 bg-red-50 dark:border-red-800/50 dark:bg-red-900/20";
      case "success":
        return "border-green-200 bg-green-50 dark:border-green-800/50 dark:bg-green-900/20";
      default:
        return "border-blue-200 bg-blue-50 dark:border-blue-800/50 dark:bg-blue-900/20";
    }
  };

  if (!user?.teamId || user?.persona === "soloUser") {
    return null; // Don't show for solo users
  }

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-card dark:to-muted/50 border-blue-200 dark:border-border hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 dark:bg-muted rounded-lg">
              <Users className="h-4 w-4 text-blue-600 dark:text-primary" />
            </div>
            <div>
              <CardTitle className="text-base text-foreground">🤝 Team AI Insights</CardTitle>
              <CardDescription className="text-xs">
                AI analysis of your team's patterns
              </CardDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => loadTeamInsights(true)}
            disabled={isLoading}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-16 bg-white/60 animate-pulse rounded-lg"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {insights.slice(0, 2).map((insight, index) => (
              <div 
                key={index} 
                className={`p-3 rounded-lg border ${getInsightColor(insight.type)}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  {getInsightIcon(insight.type)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-sm text-foreground">{insight.title}</h4>
                      <Badge 
                        variant={insight.priority === "high" ? "destructive" : insight.priority === "medium" ? "default" : "secondary"}
                        className="text-xs px-1 py-0"
                      >
                        {insight.priority}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{insight.description}</p>
                    <p className="text-xs font-medium text-foreground">{insight.actionable}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamAISuggestionsWidget;